// src/pages/Admin/UserManagementPage.jsx
// Manage system users — add, edit, deactivate, reset password.
// Route: /admin/users

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Input, Select, Switch, Tag, Tooltip,
  Modal, Popconfirm, message as antMsg, Spin,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  LockOutlined, ReloadOutlined, UserOutlined,
  CheckCircleOutlined, StopOutlined, EyeOutlined,
  EyeInvisibleOutlined, TeamOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  teal:'#0891b2',  lteal:'#ecfeff',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

const ROLES = [
  { value:'admin',   label:'Admin',   desc:'Full access — all pages, export, manage users', color:C.red,    bg:C.lred    },
  { value:'manager', label:'Manager', desc:'Read + call logging, no user management',        color:C.blue,   bg:C.lblue   },
  { value:'viewer',  label:'Viewer',  desc:'Read-only access, no exports',                   color:C.teal,   bg:C.lteal   },
];

const roleConfig = ROLES.reduce((a, r) => { a[r.value] = r; return a; }, {});

const RoleBadge = ({ role }) => {
  const r = roleConfig[role] || { label:role, color:C.text3, bg:C.surf };
  return (
    <Tag style={{ borderRadius:20, fontWeight:700, fontSize:11,
      padding:'2px 10px', border:'none',
      background:r.bg, color:r.color }}>
      {r.label}
    </Tag>
  );
};

const StatusBadge = ({ active }) => (
  <Tag style={{ borderRadius:20, fontWeight:700, fontSize:11,
    padding:'2px 10px', border:'none',
    background: active ? C.lgreen : '#f1f5f9',
    color:       active ? C.green  : C.text3 }}>
    {active ? '● Active' : '○ Inactive'}
  </Tag>
);

const emptyForm = () => ({
  username:'', email:'', fullName:'', role:'viewer',
  password:'', confirmPassword:'',
});

// ══════════════════════════════════════════════════════════════════
export default function UserManagementPage() {
  const [users,      setUsers]      = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modals
  const [addModal,   setAddModal]   = useState(false);
  const [editUser,   setEditUser]   = useState(null);  // user object
  const [pwdUser,    setPwdUser]    = useState(null);  // user object
  const [saving,     setSaving]     = useState(false);

  // Forms
  const [addForm,  setAddForm]  = useState(emptyForm());
  const [editForm, setEditForm] = useState({});
  const [pwdForm,  setPwdForm]  = useState({ newPassword:'', confirmPassword:'' });
  const [showPwd,  setShowPwd]  = useState(false);

  // ── Load ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, sRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/stats'),
      ]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : uRes.data?.data || []);
      setStats(sRes.data?.data ?? sRes.data);
    } catch { antMsg.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Filtered ─────────────────────────────────────────────────
  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterStatus === 'active'   &&  !u.isActive) return false;
    if (filterStatus === 'inactive' && u.isActive)   return false;
    if (search) {
      const s = search.toLowerCase();
      return (u.fullName||'').toLowerCase().includes(s)
          || (u.username||'').toLowerCase().includes(s)
          || (u.email||'').toLowerCase().includes(s);
    }
    return true;
  });

  // ── Validation ───────────────────────────────────────────────
  function validateAddForm() {
    if (!addForm.fullName.trim())          { antMsg.error('Full name is required'); return false; }
    if (!addForm.username.trim())          { antMsg.error('Username is required'); return false; }
    if (!/^[a-z0-9._-]{3,}$/.test(addForm.username.toLowerCase().trim())) {
      antMsg.error('Username: min 3 chars, letters/numbers/._- only'); return false;
    }
    if (!addForm.email.includes('@'))      { antMsg.error('Valid email required'); return false; }
    if (addForm.password.length < 6)       { antMsg.error('Password: min 6 characters'); return false; }
    if (addForm.password !== addForm.confirmPassword) { antMsg.error('Passwords do not match'); return false; }
    return true;
  }

  // ── Add user ─────────────────────────────────────────────────
  async function handleAdd() {
    if (!validateAddForm()) return;
    setSaving(true);
    try {
      await api.post('/users', {
        username: addForm.username.toLowerCase().trim(),
        email:    addForm.email.toLowerCase().trim(),
        password: addForm.password,
        fullName: addForm.fullName.trim(),
        role:     addForm.role,
      });
      antMsg.success(`✅ User "${addForm.fullName}" created`);
      setAddModal(false);
      setAddForm(emptyForm());
      load();
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Failed to create user');
    } finally { setSaving(false); }
  }

  // ── Edit user ─────────────────────────────────────────────────
  async function handleEdit() {
    if (!editForm.fullName?.trim()) { antMsg.error('Full name required'); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, {
        fullName: editForm.fullName.trim(),
        email:    editForm.email?.toLowerCase().trim(),
        role:     editForm.role,
      });
      antMsg.success('✅ User updated');
      setEditUser(null);
      load();
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  }

  // ── Change password ───────────────────────────────────────────
  async function handleChangePassword() {
    if (pwdForm.newPassword.length < 6) { antMsg.error('Min 6 characters'); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { antMsg.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${pwdUser.id}/password`, { newPassword: pwdForm.newPassword });
      antMsg.success(`✅ Password reset for ${pwdUser.fullName}`);
      setPwdUser(null);
      setPwdForm({ newPassword:'', confirmPassword:'' });
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Password reset failed');
    } finally { setSaving(false); }
  }

  // ── Toggle active ─────────────────────────────────────────────
  async function handleToggleActive(user) {
    try {
      const endpoint = user.isActive ? 'deactivate' : 'activate';
      await api.patch(`/users/${user.id}/${endpoint}`);
      antMsg.success(user.isActive
        ? `${user.fullName} deactivated`
        : `${user.fullName} activated`);
      load();
    } catch { antMsg.error('Failed to update status'); }
  }

  // ── Delete ────────────────────────────────────────────────────
  async function handleDelete(user) {
    try {
      await api.delete(`/users/${user.id}`);
      antMsg.success(`${user.fullName} deleted`);
      load();
    } catch { antMsg.error('Delete failed'); }
  }

  const PasswordInput = ({ value, onChange, placeholder }) => (
    <div style={{ position:'relative' }}>
      <Input
        type={showPwd ? 'text' : 'password'}
        value={value} onChange={onChange}
        placeholder={placeholder}
        style={{ borderRadius:8, paddingRight:36 }}/>
      <span onClick={() => setShowPwd(p => !p)}
        style={{ position:'absolute', right:10, top:'50%',
          transform:'translateY(-50%)', cursor:'pointer',
          color:C.text3, fontSize:14 }}>
        {showPwd ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
      </span>
    </div>
  );

  const LabelReq = ({ children }) => (
    <label style={{ display:'block', fontSize:12, fontWeight:700,
      color:C.text2, marginBottom:5 }}>
      {children} <span style={{ color:C.red }}>*</span>
    </label>
  );
  const Label = ({ children }) => (
    <label style={{ display:'block', fontSize:12, fontWeight:700,
      color:C.text2, marginBottom:5 }}>
      {children}
    </label>
  );

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div style={{ background:'#f0f4f8', minHeight:'100vh' }}>

      {/* ── Header ── */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'20px 28px', boxShadow:'0 4px 20px rgba(27,45,107,0.25)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:'#fff',
              display:'flex', alignItems:'center', gap:10 }}>
              <TeamOutlined/> User Management
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:3 }}>
              Manage who can access ErisConnect — add users, assign roles, reset passwords
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined/>}
              onClick={() => { setAddForm(emptyForm()); setAddModal(true); }}
              style={{ borderRadius:8, background:'#fff', color:C.navy,
                border:'none', fontWeight:700 }}>
              Add User
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding:'24px 28px 60px' }}>

        {/* ── Stats chips ── */}
        <div style={{ display:'grid',
          gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
          gap:12, marginBottom:20 }}>
          {[
            { icon:'👥', label:'Total Users',    value:stats?.total,    color:C.navy,   bg:'#eff6ff' },
            { icon:'✅', label:'Active',          value:stats?.active,   color:C.green,  bg:C.lgreen  },
            { icon:'⏸️', label:'Inactive',        value:stats?.inactive, color:C.text3,  bg:C.surf    },
            { icon:'🔴', label:'Admins',          value:stats?.byRole?.admin,   color:C.red,    bg:C.lred    },
            { icon:'🔵', label:'Managers',        value:stats?.byRole?.manager, color:C.blue,   bg:C.lblue   },
            { icon:'🩵', label:'Viewers',         value:stats?.byRole?.viewer,  color:C.teal,   bg:C.lteal   },
          ].map((k,i) => (
            <div key={i} style={{
              background:k.bg, borderRadius:14,
              border:`1px solid ${k.color}22`,
              borderTop:`3px solid ${k.color}`,
              padding:'14px 16px',
              boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize:20, marginBottom:4 }}>{k.icon}</div>
              <div style={{ fontSize:22, fontWeight:800, color:k.color, lineHeight:1 }}>
                {k.value ?? '—'}
              </div>
              <div style={{ fontSize:10, fontWeight:700, color:C.text3,
                textTransform:'uppercase', letterSpacing:'0.5px', marginTop:4 }}>
                {k.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16,
          background:C.white, padding:'12px 16px', borderRadius:12,
          border:`1px solid ${C.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <Input prefix={<UserOutlined style={{ color:C.text3 }}/>}
            placeholder="Search name, username, email…"
            value={search} onChange={e => setSearch(e.target.value)}
            allowClear style={{ width:260, borderRadius:8 }}/>
          <Select value={filterRole} onChange={setFilterRole} style={{ width:140 }}>
            <Select.Option value="all">All Roles</Select.Option>
            {ROLES.map(r => <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>)}
          </Select>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width:140 }}>
            <Select.Option value="all">All Status</Select.Option>
            <Select.Option value="active">● Active</Select.Option>
            <Select.Option value="inactive">○ Inactive</Select.Option>
          </Select>
          <span style={{ fontSize:12, color:C.text3, alignSelf:'center' }}>
            {filtered.length} / {users.length} users
          </span>
        </div>

        {/* ── User cards ── */}
        {loading && !users.length
          ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
          : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {filtered.map(user => (
                <div key={user.id} style={{
                  background:C.white, borderRadius:14,
                  border:`1.5px solid ${user.isActive ? C.border : '#e2e8f0'}`,
                  borderLeft:`4px solid ${user.isActive
                    ? (roleConfig[user.role]?.color || C.blue) : '#cbd5e1'}`,
                  boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
                  padding:'16px 20px', opacity: user.isActive ? 1 : 0.7,
                  transition:'box-shadow .15s',
                }}>
                  <div style={{ display:'flex', alignItems:'center',
                    justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>

                    {/* Left — user info */}
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      {/* Avatar */}
                      <div style={{
                        width:46, height:46, borderRadius:'50%', flexShrink:0,
                        background: user.isActive
                          ? `linear-gradient(135deg,${roleConfig[user.role]?.color || C.blue},${C.navy})`
                          : '#94a3b8',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:16, fontWeight:800, color:'#fff',
                      }}>
                        {user.fullName?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <span style={{ fontSize:15, fontWeight:800, color:C.text1 }}>
                            {user.fullName}
                          </span>
                          <RoleBadge role={user.role}/>
                          <StatusBadge active={user.isActive}/>
                        </div>
                        <div style={{ fontSize:12, color:C.text3, marginTop:3, display:'flex', gap:16 }}>
                          <span>@{user.username}</span>
                          <span>{user.email}</span>
                          {user.lastLoginAt && (
                            <span>Last login: {dayjs(user.lastLoginAt).fromNow()}</span>
                          )}
                          <span style={{ color:C.text3+88 }}>
                            Added {dayjs(user.createdAt).format('DD MMM YYYY')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right — actions */}
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                      {/* Active toggle */}
                      <Tooltip title={user.isActive ? 'Deactivate' : 'Activate'}>
                        <Switch size="small" checked={user.isActive}
                          onChange={() => handleToggleActive(user)}
                          style={{ background: user.isActive ? C.green : '#cbd5e1' }}/>
                      </Tooltip>

                      {/* Edit */}
                      <Tooltip title="Edit Profile">
                        <Button size="small" icon={<EditOutlined/>}
                          onClick={() => {
                            setEditUser(user);
                            setEditForm({ fullName:user.fullName, email:user.email, role:user.role });
                          }}
                          style={{ borderRadius:8 }}>
                          Edit
                        </Button>
                      </Tooltip>

                      {/* Reset password */}
                      <Tooltip title="Reset Password">
                        <Button size="small" icon={<LockOutlined/>}
                          onClick={() => {
                            setPwdUser(user);
                            setPwdForm({ newPassword:'', confirmPassword:'' });
                            setShowPwd(false);
                          }}
                          style={{ borderRadius:8, color:C.amber, borderColor:C.amber }}>
                          Reset Pwd
                        </Button>
                      </Tooltip>

                      {/* Delete */}
                      <Popconfirm
                        title={`Delete ${user.fullName}?`}
                        description="This cannot be undone. The user will lose all access."
                        onConfirm={() => handleDelete(user)}
                        okText="Delete" okButtonProps={{ danger:true }}>
                        <Button size="small" danger icon={<DeleteOutlined/>}
                          style={{ borderRadius:8 }}/>
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              ))}

              {filtered.length === 0 && (
                <div style={{ textAlign:'center', padding:60, color:C.text3,
                  background:C.white, borderRadius:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:36, marginBottom:12 }}>👤</div>
                  <div>No users match your filters</div>
                </div>
              )}
            </div>
          )
        }
      </div>

      {/* ══ ADD USER MODAL ════════════════════════════════════════ */}
      <Modal open={addModal} onCancel={() => setAddModal(false)}
        title={<span style={{ fontSize:16, fontWeight:800 }}>👤 Add New User</span>}
        footer={null} width={520}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <LabelReq>Full Name</LabelReq>
              <Input value={addForm.fullName}
                onChange={e => setAddForm(p => ({ ...p, fullName:e.target.value }))}
                placeholder="Asha Naicker" style={{ borderRadius:8 }}/>
            </div>
            <div>
              <LabelReq>Username</LabelReq>
              <Input value={addForm.username}
                onChange={e => setAddForm(p => ({ ...p, username:e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g,'') }))}
                placeholder="asha.naicker" style={{ borderRadius:8 }}
                prefix={<span style={{ color:C.text3 }}>@</span>}/>
              <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>
                Letters, numbers, ._- only
              </div>
            </div>
          </div>

          <div>
            <LabelReq>Email Address</LabelReq>
            <Input value={addForm.email}
              onChange={e => setAddForm(p => ({ ...p, email:e.target.value }))}
              placeholder="asha@erisconnect.com" style={{ borderRadius:8 }}/>
          </div>

          <div>
            <Label>Role</Label>
            <Select value={addForm.role}
              onChange={v => setAddForm(p => ({ ...p, role:v }))}
              style={{ width:'100%', borderRadius:8 }}>
              {ROLES.map(r => (
                <Select.Option key={r.value} value={r.value}>
                  <div>
                    <span style={{ fontWeight:700, color:r.color }}>{r.label}</span>
                    <span style={{ fontSize:11, color:C.text3, marginLeft:8 }}>— {r.desc}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <LabelReq>Password</LabelReq>
            <PasswordInput value={addForm.password}
              onChange={e => setAddForm(p => ({ ...p, password:e.target.value }))}
              placeholder="Min 6 characters"/>
          </div>
          <div>
            <LabelReq>Confirm Password</LabelReq>
            <PasswordInput value={addForm.confirmPassword}
              onChange={e => setAddForm(p => ({ ...p, confirmPassword:e.target.value }))}
              placeholder="Re-enter password"/>
          </div>

          {/* Role permissions info */}
          <div style={{ background:C.surf, borderRadius:10, padding:'12px 14px',
            border:`1px solid ${C.border}` }}>
            {ROLES.map(r => (
              <div key={r.value} style={{ display:'flex', gap:8, marginBottom:4,
                opacity: addForm.role === r.value ? 1 : 0.45 }}>
                <RoleBadge role={r.value}/>
                <span style={{ fontSize:12, color:C.text2 }}>{r.desc}</span>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
            <Button onClick={() => setAddModal(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleAdd}
              style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
              Create User
            </Button>
          </div>
        </div>
      </Modal>

      {/* ══ EDIT USER MODAL ═══════════════════════════════════════ */}
      <Modal open={!!editUser} onCancel={() => setEditUser(null)}
        title={<span style={{ fontSize:16, fontWeight:800 }}>✏️ Edit User — {editUser?.fullName}</span>}
        footer={null} width={480}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
          <div>
            <LabelReq>Full Name</LabelReq>
            <Input value={editForm.fullName||''}
              onChange={e => setEditForm(p => ({ ...p, fullName:e.target.value }))}
              style={{ borderRadius:8 }}/>
          </div>
          <div>
            <LabelReq>Email</LabelReq>
            <Input value={editForm.email||''}
              onChange={e => setEditForm(p => ({ ...p, email:e.target.value }))}
              style={{ borderRadius:8 }}/>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={editForm.role}
              onChange={v => setEditForm(p => ({ ...p, role:v }))}
              style={{ width:'100%', borderRadius:8 }}>
              {ROLES.map(r => (
                <Select.Option key={r.value} value={r.value}>
                  <span style={{ fontWeight:700, color:r.color }}>{r.label}</span>
                  <span style={{ fontSize:11, color:C.text3, marginLeft:8 }}>— {r.desc}</span>
                </Select.Option>
              ))}
            </Select>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
            <Button onClick={() => setEditUser(null)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleEdit}
              style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* ══ RESET PASSWORD MODAL ══════════════════════════════════ */}
      <Modal open={!!pwdUser} onCancel={() => setPwdUser(null)}
        title={<span style={{ fontSize:16, fontWeight:800 }}>🔒 Reset Password — {pwdUser?.fullName}</span>}
        footer={null} width={440}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
          <div style={{ background:C.lamber, border:`1px solid ${C.amber}44`,
            borderRadius:10, padding:'10px 14px', fontSize:12, color:C.amber }}>
            ⚠️ As admin you can reset this user's password without their current password.
            Notify them of the new password immediately.
          </div>
          <div>
            <LabelReq>New Password</LabelReq>
            <PasswordInput value={pwdForm.newPassword}
              onChange={e => setPwdForm(p => ({ ...p, newPassword:e.target.value }))}
              placeholder="Min 6 characters"/>
          </div>
          <div>
            <LabelReq>Confirm New Password</LabelReq>
            <PasswordInput value={pwdForm.confirmPassword}
              onChange={e => setPwdForm(p => ({ ...p, confirmPassword:e.target.value }))}
              placeholder="Re-enter new password"/>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
            <Button onClick={() => setPwdUser(null)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleChangePassword}
              icon={<LockOutlined/>}
              style={{ background:C.amber, border:'none', borderRadius:8, fontWeight:700 }}>
              Reset Password
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}